import { InfiniteData } from "@tanstack/react-query";
import { OrderChatMessage, OrderChatMessageStatus, OrderChatMessagesPage } from "@core/types";

const isSameMessage = (a: OrderChatMessage, b: OrderChatMessage) => {
  if (a.clientId && b.clientId && a.clientId === b.clientId) return true;
  if (a.id > 0 && b.id > 0 && a.id === b.id) return true;
  return false;
};

const mapFirstPage = (
  cache: InfiniteData<OrderChatMessagesPage> | undefined,
  fn: (items: OrderChatMessage[]) => OrderChatMessage[],
): InfiniteData<OrderChatMessagesPage> | undefined => {
  if (!cache || cache.pages.length === 0) return cache;
  const [firstPage, ...rest] = cache.pages;
  return {
    ...cache,
    pages: [{ ...firstPage, items: fn(firstPage.items) }, ...rest],
  };
};

export const mergeNewMessageIntoCache = (
  cache: InfiniteData<OrderChatMessagesPage> | undefined,
  message: OrderChatMessage,
): InfiniteData<OrderChatMessagesPage> | undefined =>
  mapFirstPage(cache, (items) => {
    const idx = items.findIndex((m) => isSameMessage(m, message));
    if (idx === -1) return [message, ...items];
    const copy = items.slice();
    copy[idx] = { ...items[idx], ...message };
    return copy;
  });

export const replaceOptimisticMessage = (
  cache: InfiniteData<OrderChatMessagesPage> | undefined,
  clientId: string,
  serverMessage: OrderChatMessage,
): InfiniteData<OrderChatMessagesPage> | undefined =>
  mapFirstPage(cache, (items) => {
    const idx = items.findIndex((m) => m.clientId === clientId);
    if (idx === -1) {
      const dup = items.findIndex((m) => m.id === serverMessage.id);
      return dup === -1 ? [serverMessage, ...items] : items;
    }
    const copy = items.slice();
    copy[idx] = { ...serverMessage, status: "sent" };
    return copy;
  });

export const setOptimisticMessageStatus = (
  cache: InfiniteData<OrderChatMessagesPage> | undefined,
  clientId: string,
  status: OrderChatMessageStatus,
): InfiniteData<OrderChatMessagesPage> | undefined =>
  mapFirstPage(cache, (items) =>
    items.map((m) => (m.clientId === clientId ? { ...m, status } : m)),
  );

export const removeMessageFromCache = (
  cache: InfiniteData<OrderChatMessagesPage> | undefined,
  messageId: number,
): InfiniteData<OrderChatMessagesPage> | undefined => {
  if (!cache) return cache;
  return {
    ...cache,
    pages: cache.pages.map((page) => ({
      ...page,
      items: page.items.filter((m) => m.id !== messageId),
    })),
  };
};
